import { useState, useMemo } from "react";
import HeaderBar from "./components/headerBar";
import { 
  FileText, 
  Code2, 
  FileArchive, 
  Download, 
  ExternalLink, 
  Search,
  Filter,
  ArrowRight
} from "lucide-react";
import Button from "../../components/common/Button";

const ResourceCard = ({ title, type, size, date, icon: Icon }) => (
  <div className="cht-group cht-p-6 cht-bg-white cht-rounded-[28px] cht-border cht-border-gray-50 hover:cht-border-[#3e57da]/20 hover:cht-shadow-[0_20px_40px_rgba(0,0,0,0.04)] cht-transition-all cht-duration-500 cht-relative cht-overflow-hidden cht-cursor-pointer">
    <div className="cht-flex cht-flex-col cht-h-full cht-space-y-6">
      {/* Icon Area */}
      <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-[20px] cht-flex cht-items-center cht-justify-center cht-group-hover:cht-bg-[#3e57da]/10 cht-transition-colors">
        <Icon size={32} className="cht-text-gray-300 group-hover:cht-text-[#3e57da] cht-transition-colors" />
      </div>
      
      {/* Meta Info */}
      <div className="cht-space-y-1">
        <h4 className="cht-text-sm cht-font-black cht-text-black cht-line-clamp-1 group-hover:cht-text-[#3e57da] cht-transition-colors">{title}</h4>
        <p className="cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest flex items-center gap-2">
          {type} <span className="cht-text-gray-200">•</span> {size}
        </p>
      </div>

      {/* Hover Details */}
      <div className="cht-pt-4 cht-border-t cht-border-gray-50 cht-flex cht-items-center cht-justify-between">
        <span className="cht-text-[10px] cht-font-bold cht-text-gray-400">Updated: {date}</span>
        <Button 
          variant="ghost" 
          size="sm" 
          className="cht-text-[#3e57da] cht-opacity-0 group-hover:cht-opacity-100"
          rightIcon={<Download size={12} />}
        >
          Download
        </Button>
      </div>
    </div>
    
    {/* Background Decorative Gradient */}
    <div className="cht-absolute -cht-right-4 -cht-top-4 cht-w-16 cht-h-16 cht-bg-[#3e57da]/5 cht-rounded-full cht-blur-xl cht-opacity-0 group-hover:cht-opacity-100 cht-transition-opacity"></div>
  </div>
);

const CategoryBlock = ({ title, count, children }) => (
  <section className="cht-space-y-6">
    <div className="cht-flex cht-items-center cht-justify-between cht-px-2">
      <div className="cht-flex cht-items-center cht-gap-3">
        <h3 className="cht-text-xl cht-font-black cht-text-black">{title}</h3>
        <span className="cht-px-2 cht-py-0.5 cht-bg-gray-100 cht-text-[10px] cht-font-bold cht-text-gray-400 cht-rounded-md">{count} Files</span>
      </div>
      <Button variant="ghost" size="sm" className="cht-text-[#3e57da]">View All</Button>
    </div>
    <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
      {children}
    </div>
  </section>
);

const Resources = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const resourcesData = [
    {
      category: "Source Code & Templates",
      items: [
        { title: "E-commerce-Auth-Logic.zip", type: "Source Code", size: "2.4 MB", date: "Oct 24, 2026", icon: Code2 },
        { title: "Django-Rest-Framework-Skeleton", type: "Template", size: "5.8 MB", date: "Oct 20, 2026", icon: FileArchive },
        { title: "React-Context-State-Manager", type: "Source Code", size: "1.2 MB", date: "Oct 18, 2026", icon: Code2 },
        { title: "Tailwind-Dashboard-Layout", type: "Template", size: "3.1 MB", date: "Oct 15, 2026", icon: FileArchive },
      ]
    },
    {
      category: "PDF Guides & Documentation",
      items: [
        { title: "Django-Reference-Handbook.pdf", type: "Guide", size: "12.4 MB", date: "Oct 22, 2026", icon: FileText },
        { title: "SQL-Normalization-Advanced.pdf", type: "PDF Guide", size: "4.2 MB", date: "Oct 19, 2026", icon: FileText },
        { title: "Redux-Toolkit-Deep-Dive.pdf", type: "Documentation", size: "8.1 MB", date: "Oct 14, 2026", icon: FileText },
        { title: "Career-Path-Fullstack.pdf", type: "Guide", size: "1.5 MB", date: "Oct 10, 2026", icon: FileText },
      ]
    }
  ];

  const filteredResources = useMemo(() => {
    return resourcesData.map(group => ({
      ...group,
      items: group.items.filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = activeCategory === "All" || group.category === activeCategory;
        return matchesSearch && matchesCategory;
      })
    })).filter(group => group.items.length > 0);
  }, [searchQuery, activeCategory]);

  const categories = ["All", "Source Code & Templates", "PDF Guides & Documentation"];

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Digital Resources" />
      
      <main className="cht-container cht-p-0 md:cht-p-6 cht-space-y-12 pb-12">
        
        {/* Resource Search & Filter */}
        <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-justify-between cht-gap-6">
          <div className="cht-relative cht-flex-1 cht-max-w-2xl cht-group cht-w-full">
            <Search className="cht-absolute cht-left-5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400 group-focus-within:cht-text-[#3e57da] cht-transition-colors" size={20} />
            <input 
              type="text" 
              placeholder="Search source code, guides, or cheatsheets..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="cht-w-full cht-bg-white cht-border-none cht-rounded-3xl cht-py-5 cht-pl-14 cht-pr-6 cht-text-sm cht-font-bold cht-shadow-[0_10px_30px_rgba(0,0,0,0.02)] cht-outline-none cht-focus:cht-ring-2 cht-focus:cht-ring-[#3e57da]/10 cht-transition-all"
            />
          </div>
          <div className="cht-flex cht-items-center cht-gap-3 cht-w-full md:cht-w-auto">
            <div className="cht-relative">
              <select 
                value={activeCategory}
                onChange={(e) => setActiveCategory(e.target.value)}
                className="cht-appearance-none cht-bg-white cht-border-none cht-rounded-2xl cht-py-4 cht-pl-12 cht-pr-8 cht-text-xs cht-font-bold cht-shadow-sm cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 cht-transition-all cht-cursor-pointer"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <Filter size={16} className="cht-absolute cht-left-5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" />
            </div>
            <Button 
              variant="secondary"
              className="cht-flex-1 md:cht-flex-none"
              onClick={() => {setSearchQuery(""); setActiveCategory("All");}}
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Categories */}
        <div className="cht-space-y-16">
          
          {filteredResources.length > 0 ? (
            filteredResources.map((group, idx) => (
              <CategoryBlock key={idx} title={group.category} count={group.items.length}>
                {group.items.map((item, i) => (
                  <ResourceCard key={i} {...item} />
                ))}
              </CategoryBlock>
            ))
          ) : (
            <div className="cht-text-center cht-py-20 cht-space-y-4">
              <div className="cht-w-20 cht-h-20 cht-bg-gray-50 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto">
                <Search size={32} className="cht-text-gray-200" />
              </div>
              <h3 className="cht-text-lg cht-font-bold cht-text-black">No resources found</h3>
              <p className="cht-text-sm cht-text-gray-400 cht-max-w-xs cht-mx-auto">We couldn't find any assets matching your search criteria. Try using different keywords.</p>
            </div>
          )}

          {/* Quick Support Section */}
          <div className="cht-bg-gradient-to-br cht-from-[#1e293b] cht-to-[#0f172a] cht-p-10 cht-rounded-[48px] cht-text-white cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-justify-between cht-gap-8 cht-shadow-2xl cht-relative cht-overflow-hidden">
            <div className="cht-space-y-4 cht-relative cht-z-10">
              <h3 className="cht-text-3xl cht-font-black">Missing a resource?</h3>
              <p className="cht-text-white/60 cht-max-w-md cht-font-medium leading-relaxed">If you need a specific code snippet or guide that isn't listed here, request it from our team and we'll upload it within 24 hours.</p>
              <Button 
                variant="ghost" 
                className="cht-text-[#3e57da] cht-p-0 hover:cht-bg-transparent"
                rightIcon={<ArrowRight size={18} />}
              >
                Open Support Ticket
              </Button>
            </div>
            <div className="cht-w-full md:cht-w-fit cht-relative cht-z-10">
              <Button 
                variant="primary" 
                size="lg" 
                className="cht-w-full cht-rounded-[24px]"
              >
                Request Asset
              </Button>
            </div>
            {/* Background elements */}
            <div className="cht-absolute -cht-left-20 -cht-bottom-20 cht-w-64 cht-h-64 cht-bg-[#3e57da]/10 cht-rounded-full cht-blur-3xl"></div>
          </div>

        </div>

      </main>
    </section>
  );
};

export default Resources;
